// Voice-score interpretation engine (round-15, 2026-08-31). Pure functions, no I/O — given the
// CURRENT game score and a heard "X-Y" (spoken order), decide what it means:
//
//  - Scores only ever move FORWARD: a candidate (L,R) is legal iff L >= curL, R >= curR and at
//    least one point was added. 5-2 can never become 1-1.
//  - A heard pair may be reversed relative to the table ("5-2" when the table is 2-5). Both
//    orientations are tried; if EXACTLY ONE is legal it wins. Both legal = ambiguous (no
//    action), neither = reject.
//  - An ANCHOR resolves ambiguity when the speaker gave one: "5-2 rojo" anchors the FIRST
//    spoken number to the red side's card; "5-2 gano yo"/"5-2 mío" anchors the first number to
//    the SPEAKER's side (caller resolves the speaker to a side via voice fingerprints first).
//  - The total point delta is capped (MAX_DELTA) — a wildly-far score is far more likely a
//    misread than 5+ unrecorded points.
import type { Side } from './types'

export const MAX_DELTA = 4

export interface ScoreState {
  left: number
  right: number
}

export type Interpretation =
  | { action: 'apply'; left: number; right: number; addLeft: number; addRight: number }
  | { action: 'ambiguous' }
  | { action: 'reject'; reason: 'backwards' | 'too_far' | 'no_change' }

function legality(cur: ScoreState, cand: ScoreState): Interpretation {
  const dl = cand.left - cur.left
  const dr = cand.right - cur.right
  if (dl < 0 || dr < 0) return { action: 'reject', reason: 'backwards' }
  if (dl + dr === 0) return { action: 'reject', reason: 'no_change' }
  if (dl + dr > MAX_DELTA) return { action: 'reject', reason: 'too_far' }
  return { action: 'apply', left: cand.left, right: cand.right, addLeft: dl, addRight: dr }
}

/**
 * Interpret a heard score pair `[a, b]` (spoken order) against the current game score.
 * `anchorFirstTo` fixes which physical side the FIRST spoken number belongs to (from a color
 * word or an identified speaker); without it both orientations are tried and exactly one legal
 * orientation must exist.
 */
export function interpretScore(cur: ScoreState, heard: [number, number], anchorFirstTo?: Side): Interpretation {
  const [a, b] = heard
  const asSpoken = legality(cur, { left: a, right: b })
  const reversed = legality(cur, { left: b, right: a })

  if (anchorFirstTo === 'left') return asSpoken
  if (anchorFirstTo === 'right') return reversed

  const spokenOk = asSpoken.action === 'apply'
  const reversedOk = reversed.action === 'apply'
  if (spokenOk && reversedOk) {
    // Same candidate both ways (e.g. "3-3") is not ambiguous.
    if (a === b) return asSpoken
    return { action: 'ambiguous' }
  }
  if (spokenOk) return asSpoken
  if (reversedOk) return reversed
  // Neither works — prefer the more informative rejection (backwards beats too_far).
  const reasons = [asSpoken, reversed].map((r) => (r.action === 'reject' ? r.reason : 'no_change'))
  return { action: 'reject', reason: reasons.includes('backwards') && reasons.includes('too_far') ? 'backwards' : (reasons[0] as 'backwards' | 'too_far' | 'no_change') }
}

/** Cosine similarity between two x-vectors (voice fingerprints). */
export function cosine(a: number[], b: number[]): number {
  let dot = 0
  let na = 0
  let nb = 0
  const n = Math.min(a.length, b.length)
  for (let i = 0; i < n; i++) {
    dot += a[i] * b[i]
    na += a[i] * a[i]
    nb += b[i] * b[i]
  }
  if (na === 0 || nb === 0) return 0
  return dot / (Math.sqrt(na) * Math.sqrt(nb))
}

/** Minimum cosine similarity for a fingerprint to count as "this player spoke". */
export const SPEAKER_MATCH_THRESHOLD = 0.55

// Round-16 (2026-08-31): x-vectors are model-specific — a vector from a future spk-model swap
// is not comparable to one from vosk-model-spk-0.4 (see kmb_stt.py's SPK_MODEL_VERSION and
// safe-by-design.md's version-stamp convention). Every install so far has only ever run
// vosk-model-spk-0.4, so a null/undefined stamp on either side is treated as that value
// (legacy rows predate the stamp), not as "unknown, refuse to compare".
export const LEGACY_SPK_MODEL_VERSION = 'vosk-model-spk-0.4'

function normalizedSpkModelVersion(v: string | null | undefined): string {
  return v ?? LEGACY_SPK_MODEL_VERSION
}

/** Whether two spk vectors come from the same embedding space and may be cosine-compared. */
export function sameSpkModel(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizedSpkModelVersion(a) === normalizedSpkModelVersion(b)
}

export interface SpeakerCandidate {
  id: number
  centroid: number[]
  spkModelVersion?: string | null
}

export interface RankedSpeakerCandidate {
  id: number
  similarity: number
  aboveThreshold: boolean
  skippedModelMismatch: boolean // candidate's centroid is from a different spk model — not comparable
}

/** Every enrolled candidate ranked by cosine similarity to a heard x-vector, best first.
 *  Candidates from a different spk model (see sameSpkModel) are included but flagged
 *  `skippedModelMismatch` with similarity 0 rather than silently dropped, so the debug UI can
 *  show the guard doing its job instead of just showing fewer rows. */
export function rankSpeakerCandidates(
  spk: number[],
  spkModelVersion: string | null | undefined,
  candidates: SpeakerCandidate[],
): RankedSpeakerCandidate[] {
  return candidates
    .map((c) => {
      const comparable = sameSpkModel(spkModelVersion, c.spkModelVersion)
      const sim = comparable ? cosine(spk, c.centroid) : 0
      return {
        id: c.id,
        similarity: sim,
        aboveThreshold: comparable && sim >= SPEAKER_MATCH_THRESHOLD,
        skippedModelMismatch: !comparable,
      }
    })
    .sort((a, b) => b.similarity - a.similarity)
}

/** Best-matching candidate for a heard x-vector, or null if none clears the threshold — skips
 *  any candidate whose centroid was stamped with a different spk model (see sameSpkModel). */
export function bestSpeakerMatch(
  spk: number[],
  spkModelVersion: string | null | undefined,
  candidates: SpeakerCandidate[],
): { id: number; similarity: number } | null {
  const ranked = rankSpeakerCandidates(spk, spkModelVersion, candidates)
  const best = ranked[0]
  return best && best.aboveThreshold ? { id: best.id, similarity: best.similarity } : null
}

// -------------------------------------------------------------------------------------------
// Round-20 (2026-08-31): calibrated speaker-match confidence. Live testing showed raw cosine
// displayed as a "%" is misleading — a ~1s "soy rojo" scored 29% against the speaker's OWN
// centroid, while longer utterances trivially score much higher, so the number tracked audio
// length far more than actual identity. speakerConfidence() replaces raw cosine as the headline
// number EVERYWHERE a match % is shown (DebugStt's Speaker match panel, NewMatch's corner-claim
// popup, LiveMatch's anchor/point-claim popups) — raw cosine still gets a small secondary
// display where it's useful for debugging (DebugStt).
//
// Shape: softmax over the candidate cosines PLUS a fixed phantom "impostor" candidate — with
// only one enrolled profile there's no real runner-up, so the phantom stands in for "it could
// still be nobody", guaranteeing there is always competing probability mass and the winner can
// never reach 100% (same reason an LLM's softmax token distribution never reaches probability 1
// even for an overwhelming favorite). The softmax result is then scaled by a duration-quality
// factor in (0, 1] so a short utterance's ceiling is capped well below the long-utterance
// ceiling — a 1s clip cannot claim near-certainty no matter how clean the cosine match is.
//
// Round-21 (2026-08-31, live-testing rebalance): live test measured raw cosine 0.31 against the
// SOLE enrolled profile, ~1s utterance -> only 19% calibrated. Root cause: IMPOSTOR_BASELINE_COSINE
// (0.30) sat essentially AT the real score (0.31), so the softmax split ~50/50 between "it's him"
// and "it's the phantom" before duration even applied, and the old DURATION_KNEE_SEC (3.0s) capped
// a 1s utterance's ceiling at 46.7% regardless of match quality — mathematically unable to reach
// 50% no matter how clean the cosine. Two changes, not one: the phantom baseline came down to
// where real cross-speaker cosines with this model actually sit (near 0-0.15, not 0.30 — 0.30 was
// a guess, not measured), which does most of the work (a sole real candidate now beats a
// believable-but-lower impostor by a much wider margin, so being the ONLY candidate above the
// pre-filter carries real weight); the duration knee also came down (3.0s -> 1.5s) since 3s of
// continuous speech is an unrealistic bar for "cinco-dos gano yo"-length claims and was crushing
// every short-but-legitimate utterance's ceiling even after the baseline fix. No separate
// "sole-candidate bonus" term was needed — lowering the impostor baseline already concentrates
// softmax mass on a real solo candidate; a bonus on top risked breaking the near-tie-splits-~50/50
// property. Sanity re-run (old -> new): solo 0.31 cos/1.0s 24.0%->50.0% (target case, ~19-24% was
// measured/modeled before, now hits the requested ~50%); solo 0.31 cos/0.3s 14.4%->24.5% (short
// utterances still meaningfully capped); solo 0.31 cos/unknown-duration 10.3%->13.6%; clean solo
// match 0.55 cos/3s 80.0%->84.8%, 0.65 cos/3s 87.5%->89.3% (still comfortably in the 70-90% design
// band); near-tie 0.50/0.46 cos/3s 46.9%/37.6% -> 48.3%/41.1% (still splits close together, never
// reaches 100% either side).
//
// Second anchor (round-21, same session): the user also measured the LONGER corner-claim phrase
// (~2.5s, round-22's "yo juego en el campo rojo/azul") under the OLD constants: sole candidate,
// 37% calibrated. Back-solving that against the old formula implies a raw cosine of ~0.247 for
// that utterance (duration alone, not a better cosine, drove 19%->37% under the old constants).
// Feeding the SAME implied cosine through the NEW constants at 2.5s (>= the new 1.5s knee, so
// duration quality is already at its 1.0 ceiling) gives 62.4% — meaningfully higher than the 50%
// short-phrase target, consistent with "more speech -> more confidence" even without assuming
// the cosine itself improves. If the longer utterance's real cosine also comes in higher
// (plausible — more speech usually gives the x-vector extractor more to work with), the new
// formula scales accordingly: 0.31 cos/2.5s -> 68.1%, 0.40 cos/2.5s -> 75.4%, 0.50 cos/2.5s ->
// 82.1%. Only real logged cosines (DESIGN.md's Observability section — corner-claim attempts
// now ship their raw cosine) can confirm which regime actually holds; the constants were not
// further tuned against this single unconfirmed data point.
export const CONFIDENCE_TEMPERATURE = 0.25 // softmax temperature — controls how sharply a cosine
// margin separates into a confidence gap. Raised from 0.18 alongside the lower impostor baseline
// so the clean-match band (best ~0.5-0.65 against the new ~0.12 impostor, i.e. a 0.38-0.53 gap)
// still lands the winner around 70-90%, and a near-tie margin (~0.03-0.05 gap) still lands both
// candidates close together (~42-48% each, rest to the phantom). Smaller = sharper (more decisive
// on small gaps); larger = flatter (more conservative, needs a bigger margin to commit).
export const IMPOSTOR_BASELINE_COSINE = 0.12 // phantom "not any enrolled speaker" background
// candidate injected into every softmax so a single-profile roster still has real competition
// instead of a free pass toward 100%. Lowered from 0.30 (round-21) — that value was an
// unmeasured guess and sat right on top of real single-candidate scores; real DIFFERENT-speaker
// x-vector cosines with vosk-model-spk-0.4 sit near 0-0.15, not 0.30. Still comfortably below
// SPEAKER_MATCH_THRESHOLD so it never itself reads as a match.
export const DURATION_KNEE_SEC = 1.5 // utterance length (s) at which the duration-quality factor
// reaches 1.0 (full confidence range unlocked). Lowered from 3.0 (round-21) — 3s of continuous
// speech was an unrealistic bar for a normal claim ("soy rojo"/"cinco-dos gano yo" runs ~1-1.5s)
// and was capping every short-but-legitimate utterance's confidence ceiling regardless of match
// quality; below this, quality ramps down linearly.
export const MIN_DURATION_QUALITY = 0.2 // floor on the duration-quality factor so an ultra-short
// or duration-less utterance still carries *some* signal (never hard-zeroed to 0%).

/** Duration -> quality factor in [MIN_DURATION_QUALITY, 1]. Unknown/invalid duration is treated
 *  conservatively (the floor), same as "too short to say much". */
function durationQuality(durationSec: number | null | undefined): number {
  if (durationSec == null || !Number.isFinite(durationSec) || durationSec <= 0) return MIN_DURATION_QUALITY
  const t = Math.max(0, Math.min(1, durationSec / DURATION_KNEE_SEC))
  return MIN_DURATION_QUALITY + (1 - MIN_DURATION_QUALITY) * t
}

/**
 * Calibrated confidence for a set of candidate cosine similarities (best-effort order doesn't
 * matter — this returns confidences aligned 1:1 with `candidateSimilarities`), given the
 * utterance's duration. This is THE function every "%"-displaying surface calls — never show
 * raw cosine as the headline number.
 *
 * Softmax over [...candidateSimilarities, IMPOSTOR_BASELINE_COSINE] (temperature
 * CONFIDENCE_TEMPERATURE), scaled by durationQuality(durationSec). Asymptotically < 1: the
 * phantom impostor always holds some probability mass, and the duration factor is itself capped
 * at 1 — no return path reaches exactly 1.0.
 */
export function speakerConfidence(candidateSimilarities: number[], durationSec: number | null | undefined): number[] {
  if (candidateSimilarities.length === 0) return []
  const withPhantom = [...candidateSimilarities, IMPOSTOR_BASELINE_COSINE]
  const maxSim = Math.max(...withPhantom)
  const exps = withPhantom.map((s) => Math.exp((s - maxSim) / CONFIDENCE_TEMPERATURE))
  const sumExps = exps.reduce((a, b) => a + b, 0)
  const dq = durationQuality(durationSec)
  return candidateSimilarities.map((_, i) => (exps[i] / sumExps) * dq)
}

/** Calibrated confidence needed for an anchor/corner-claim match to actually be ACTED on (vs.
 *  just shown). Deliberately separate from SPEAKER_MATCH_THRESHOLD (a raw-cosine floor used only
 *  to decide whether a candidate is even in the running, e.g. rankSpeakerCandidates'
 *  aboveThreshold) — this is the calibrated-score gate real actions check. */
export const CONFIDENCE_ACCEPT_THRESHOLD = 0.5

export interface SpeakerConfidenceMatch {
  id: number
  similarity: number
  confidence: number
}

/** rankSpeakerCandidates()'s result, promoted to calibrated confidence — one candidate per
 *  comparable entry (skippedModelMismatch entries are excluded, they're not real competitors),
 *  in the same best-first order. */
export function rankedSpeakerConfidences(
  ranked: RankedSpeakerCandidate[],
  durationSec: number | null | undefined,
): SpeakerConfidenceMatch[] {
  const comparable = ranked.filter((c) => !c.skippedModelMismatch)
  const confidences = speakerConfidence(
    comparable.map((c) => c.similarity),
    durationSec,
  )
  return comparable.map((c, i) => ({ id: c.id, similarity: c.similarity, confidence: confidences[i] }))
}

/** Best candidate by calibrated confidence, or null if nothing clears CONFIDENCE_ACCEPT_THRESHOLD
 *  (or there are no comparable candidates at all). This is the confidence-gated counterpart to
 *  bestSpeakerMatch() — use this one for anything that ACTS on the match (corner claim, anchor
 *  resolution); bestSpeakerMatch's raw-cosine threshold stays as the "is this even a candidate"
 *  filter feeding rankSpeakerCandidates. */
export function bestSpeakerConfidence(
  ranked: RankedSpeakerCandidate[],
  durationSec: number | null | undefined,
): SpeakerConfidenceMatch | null {
  const confidences = rankedSpeakerConfidences(ranked, durationSec)
  if (confidences.length === 0) return null
  const best = confidences.reduce((a, b) => (b.confidence > a.confidence ? b : a))
  return best.confidence >= CONFIDENCE_ACCEPT_THRESHOLD ? best : null
}

// -------------------------------------------------------------------------------------------
// Round-27 (2026-08-31): confidence BANDS for corner-claim voice confirmation. Previously a
// corner claim was strictly binary — >= CONFIDENCE_ACCEPT_THRESHOLD assigned immediately,
// anything below was silently dropped (toast + track only, per the standing false-negative-
// over-false-positive rule). User feedback: a claim that's clearly heading toward one specific
// candidate but doesn't clear the auto-accept bar shouldn't be thrown away OR silently assigned
// — the table should ASK ("¿Eres Luuk?") and let a yes/no answer (voice or tap) settle it. This
// adds a middle band, "dubious", between the existing "high" (auto-assign, unchanged) and "low"
// (no action, unchanged) bands — see services/confirmFlow.ts for the state machine that acts on
// it and DESIGN.md's corner-claim-confirmation section for the full flow.
//
// A "dubious but clear winner" needs BOTH:
//  - CONFIRM_FLOOR <= confidence < CONFIDENCE_ACCEPT_THRESHOLD — some real signal, just short of
//    the auto-accept bar. Below the floor, per the false-negative-biased design, the table
//    doesn't even bother asking — an ask has a real cost (interrupts the room, needs an answer),
//    and asking about pure noise would itself become the kind of false-positive-adjacent
//    annoyance this whole system exists to avoid.
//  - A CLEAR MARGIN over the runner-up (the second-best REAL candidate's calibrated confidence,
//    or 0 when there's only one comparable candidate — the phantom impostor is already priced
//    into each candidate's own confidence via speakerConfidence()'s softmax, so it never counts
//    as a second "real" competitor here). A single enrolled candidate's margin is always its own
//    full confidence, so a lone plausible candidate always clears CONFIRM_MIN_MARGIN trivially;
//    the margin gate exists specifically to catch the OTHER case — two-plus enrolled players
//    near a genuine tie — where asking about one specific name would be a coin flip dressed up as
//    a question, not an ask worth making.
//
// Numbers picked against this file's own measured examples (rounds 20-21 above): the near-tie
// case (0.50/0.46 cosine, 3s -> 48.3%/41.1% calibrated) has a 7.2-point gap — MUST stay out of
// "dubious" (both candidates are too live to single one out), which needs CONFIRM_MIN_MARGIN
// clearly above 7.2. A short solo utterance (0.31 cosine, 0.3s -> 24.5%) is exactly the shape of
// claim worth asking about — real signal, just short on duration — which needs CONFIRM_FLOOR at
// or below 24.5. The same cosine with no known duration (13.6%) is weak enough to stay silent on.
export const CONFIRM_FLOOR = 0.2 // 20% calibrated confidence — below this, stay silent (existing
// "low" behavior: toast + track, no ask). Chosen below the measured 24.5% short-utterance anchor
// (still worth asking about) and above the 13.6% unknown-duration anchor (too weak to act on at
// all, even as a question).
export const CONFIRM_MIN_MARGIN = 0.15 // 15 points of calibrated-confidence gap over the runner-
// up (or over 0 when there's no second real candidate). Clears the measured near-tie's 7.2-point
// gap by a comfortable margin (stays "low"/ambiguous, no ask) while never blocking a genuine
// sole-candidate claim (whose margin is always its own full confidence).

export type ConfidenceBand = 'high' | 'dubious' | 'low'

export interface ConfidenceBandResult {
  band: ConfidenceBand
  /** Best candidate by calibrated confidence, or null if there are no comparable candidates at
   *  all (band is always 'low' in that case). */
  top: SpeakerConfidenceMatch | null
  /** Second-best REAL candidate (never the phantom impostor — see speakerConfidence), or null
   *  when there's only one (or zero) comparable candidates. */
  runnerUp: SpeakerConfidenceMatch | null
  /** top.confidence - (runnerUp?.confidence ?? 0); null only when `top` itself is null. */
  margin: number | null
}

// Round-28 (2026-08-31): live-match point-claim speaker matching (candidateScope.ts's 'match'
// tier, DESIGN.md's candidate-scope section) is the first caller with a genuine, small,
// consistently-populated runner-up — corner claims mostly ran against either a single enrolled
// profile or a large 'all enrolled' pool where a close 2nd place was rare in practice, but a
// live match ALWAYS has exactly one real competitor (the other player). Re-examining the 'high'
// band under this lens found a gap: 'high' only ever checked top.confidence >=
// CONFIDENCE_ACCEPT_THRESHOLD, no margin requirement at all — fine when a margin near 0 could
// only happen with a near-tie top that itself couldn't clear 50% (true for every example this
// file had measured so far), but NOT structurally guaranteed once two real, similarly-trained
// voices can both score respectably. Worked example: cosines 0.55 (top) / 0.50 (runner-up), 3s
// (duration factor already at ceiling) -> 50.1% / 41.0% calibrated — top JUST clears
// CONFIDENCE_ACCEPT_THRESHOLD, but the runner-up is only a 9.1-point margin behind, not the kind
// of "clear winner" the whole confidence-band design otherwise insists on before acting. Old
// logic: auto-applies (band='high', no ask). Fixed logic below: a runner-up within
// CONFIRM_MIN_MARGIN of a JUST-clearing top downgrades 'high' -> 'dubious' — still asks (the
// top candidate's confidence is real signal, not discarded), never silently applies a contested
// call. Sanity re-run against this file's constants (2-way, 3s/ceiling duration):
//   - clean 2-way (0.60 / 0.20 cos): 74.2% / 15.0%, margin 59.2pts -> 'high' (unaffected, wide
//     enough margin to auto-apply, exactly as before).
//   - contested near-both-plausible (0.55 / 0.50 cos): 50.1% / 41.0%, margin 9.1pts -> WAS
//     'high' (auto-apply), NOW 'dubious' (asks) — the fix's actual target case.
//   - true near-tie (0.50 / 0.50 cos): 45.1% / 45.1%, margin 0 -> 'low' either way (top itself
//     never reaches 50%) — "a genuinely ambiguous 50/50 must NOT auto-apply" holds, unaffected.
// A single (or zero) comparable candidate is NEVER affected: with no runnerUp, margin is always
// the top's own full (>= 50%) confidence, trivially >= CONFIRM_MIN_MARGIN — every existing
// solo-candidate 'high' example in this file's round-20/21 sanity re-runs (e.g. 84.8%/89.3% clean
// solo matches) is unchanged. This also quietly firms up corner_claim's own 'all'/'picked' scopes
// (candidateScope.ts) whenever the enrolled roster happens to produce a close 2nd place — a
// strictly MORE conservative change everywhere, consistent with the standing false-negative-
// over-false-positive bias; never loosens anything.

/** Corner-claim-specific classification: same ranked/duration inputs as bestSpeakerConfidence,
 *  but returns which of the three bands applies (see the round-27/round-28 docblocks above)
 *  instead of a binary accept/reject. Also the function LiveMatch.vue's in-match anchor/
 *  point-claim speaker matching now uses (round-28) — see DESIGN.md's candidate-scope section. */
export function classifyConfidenceBand(
  ranked: RankedSpeakerCandidate[],
  durationSec: number | null | undefined,
): ConfidenceBandResult {
  const confidences = rankedSpeakerConfidences(ranked, durationSec) // best-first
  const top = confidences[0] ?? null
  if (!top) return { band: 'low', top: null, runnerUp: null, margin: null }
  const runnerUp = confidences[1] ?? null
  const margin = top.confidence - (runnerUp?.confidence ?? 0)
  if (top.confidence >= CONFIDENCE_ACCEPT_THRESHOLD) {
    // Round-28: a real, close runner-up contests an otherwise-clearing top — ask rather than
    // silently commit to a coin-flip-adjacent call. See the docblock above for worked numbers.
    if (runnerUp && margin < CONFIRM_MIN_MARGIN) return { band: 'dubious', top, runnerUp, margin }
    return { band: 'high', top, runnerUp, margin }
  }
  if (top.confidence >= CONFIRM_FLOOR && margin >= CONFIRM_MIN_MARGIN) return { band: 'dubious', top, runnerUp, margin }
  return { band: 'low', top, runnerUp, margin }
}
